import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { AutoNumericDirective } from '../../../directives/auto-numeric.directive';
import { BaseUI } from '../../base.ui';
import { ErrorDialog } from '../../dialog/error/error.dialog';
import {
    GetContractDataUseCase,
    GetMyProductsUseCase,
    GetProductUseCase,
    OrderAction,
    ProductInterface,
    SelectedProductInterface,
    Transaction,
} from '../../../../core';
import { Id } from '../../../domain/data';
import { LocalStorage } from '../../../client/helpers/local-storage';
import { ParentEvents } from '../../../utils/parent-events.service';

@Component({
    selector: 'thr-operate-refund-internal-transfer',
    templateUrl: './operate-refund-internal-transfer.component.html',
    styleUrls: ['./operate-refund-internal-transfer.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class OperateRefundInternalTransferComponent extends BaseUI implements AfterViewInit, OnDestroy, OnInit {
    public OrderAction: typeof OrderAction = OrderAction;

    public initialAmount;
    public initialShares;
    public internalProduct: ProductInterface;
    public product: ProductInterface;

    @Input()
    editable = true;

    @Input()
    selectedProduct: SelectedProductInterface;

    @ViewChild('sharesDirective')
    sharesDirective: AutoNumericDirective;

    @ViewChild('amountDirective')
    amountDirective: AutoNumericDirective;

    @Input()
    canExonerate = false;

    @Output()
    detail: EventEmitter<Id> = new EventEmitter();

    @Output()
    exonerate: EventEmitter<Id> = new EventEmitter();


    constructor(
        parentEvents: ParentEvents,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private getContractDataUseCase: GetContractDataUseCase,
        private getMyProductsUseCase: GetMyProductsUseCase,
        private getProductUseCase: GetProductUseCase,
        private localStorage: LocalStorage
    ) {
        super(parentEvents);
    }

    ngOnInit() {
        this.initialShares = this.selectedProduct.order.internalTransferShares || 0;
        this.initialAmount = this.selectedProduct.order.internalTransferAmount || 0;
        this.cdr.detectChanges();

        this.transformSelectedProduct();
    }

    get maximumInvestment(): number {
        if (this.internalProduct) {
            if (this.selectedProduct.order.action === OrderAction.AMOUNT) {
                return Number((this.internalProduct.amount - this.internalProduct.minimumAmountToMaintain).toFixed(2));
            }

            return null;
        }

        return 0;
    }

    get maximumShares(): number {
        if (this.internalProduct) {
            if (this.selectedProduct.order.action === OrderAction.SHARES) {
                const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0.00000001;
                const minimumSharesToMaintain = this.internalProduct.minimumAmountToMaintain / sharePrice;
                return Number((this.internalProduct.shares - minimumSharesToMaintain).toFixed(8));
            }

            return null;
        }

        return 0;
    }

    private transformSelectedProduct() {
        this.getMyProductsUseCase
            .bind()
            .pipe(
                tap((transaction) => {
                    transaction.fold(
                        (myProducts) => {
                            this.internalProduct = myProducts.find((it) => it.encryptedCode.equals(this.selectedProduct.encryptedCode));
                            this.selectedProduct.order.internalTransferCode = String(this.internalProduct.encryptedCode);
                        },
                        (throwable) =>
                            this.dialog.open(ErrorDialog, {
                                data: { text: throwable.message },
                            })
                    );
                }),
                switchMap((_) =>
                    this.getContractDataUseCase.bind().pipe(
                        switchMap((transactionData) =>
                            transactionData.map(
                                (data) => {
                                    if (data.model.pivotFund.id) {
                                        return this.getProductUseCase.bind({
                                            productCode: data.model.pivotFund.id,
                                        });
                                    }

                                    return of(Transaction.error<ProductInterface>());
                                },
                                (throwable) => of(Transaction.error(throwable.message))
                            )
                        )
                    )
                )
            )
            .subscribe((transaction) => {
                transaction.fold(
                    (product) => {
                        if (this.localStorage.relatedContractCode) {
                            this.selectedProduct.order.destinationContractCode = Id.fromString(this.localStorage.relatedContractCode);
                        }

                        this.selectedProduct.productId = product.id;
                        this.selectedProduct.code = product.code;
                        this.selectedProduct.encryptedCode = product.encryptedCode;
                        this.selectedProduct.isin = product.isin;
                        this.selectedProduct.name = product.name;

                        this.product = product;
                        this.cdr.detectChanges();

                        if (this.editable) {
                            if (!this.selectedProduct.order.amount || this.selectedProduct.order.amount === 0) {
                                this.selectedProduct.order.action = OrderAction.TOTAL;
                            }
                            this.onActionChange();
                        }
                    },
                    (throwable) =>
                        this.dialog.open(ErrorDialog, {
                            data: { text: throwable.message },
                        })
                );
            });
    }

    onActionChange() {
        this.selectedProduct.order.internalTransferAmount = 0;
        this.selectedProduct.order.internalTransferShares = 0;
        this.cdr.detectChanges();

        switch (this.selectedProduct.order.action) {
            case OrderAction.TOTAL:
                this.selectedProduct.order.withShares = true;
                this.onTotalChange();
                break;
            case OrderAction.SHARES:
                this.selectedProduct.order.withShares = true;
                this.onSharesChange();
                break;
            case OrderAction.AMOUNT:
                this.selectedProduct.order.withShares = false;
                this.onAmountChange();
                break;
        }

        this.parentEvents.resize();
    }

    onResize() {
        this.parentEvents.resize();
    }

    onTotalChange() {
        setTimeout(() => {
            this.selectedProduct.order.internalTransferAmount = this.internalProduct.amount;
            this.selectedProduct.order.internalTransferShares = this.internalProduct.shares;

            this.selectedProduct.order.amount = this.internalProduct.amount;
            this.selectedProduct.order.shares = this.product
                ? Number((this.selectedProduct.order.amount / this.product.lastPrice).toFixed(8))
                : 0;

            if (this.sharesDirective) {
                this.sharesDirective.value = this.selectedProduct.order.internalTransferShares;
            }
            if (this.amountDirective) {
                this.amountDirective.value = this.selectedProduct.order.internalTransferAmount;
            }

            this.cdr.detectChanges();
        });
    }

    onSharesChange() {
        setTimeout(() => {
            if (this.sharesDirective) {
                this.selectedProduct.order.internalTransferShares = this.sharesDirective.value;
            }

            const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0;
            this.selectedProduct.order.internalTransferAmount = Number(
                (this.selectedProduct.order.internalTransferShares * sharePrice).toFixed(2)
            );

            this.selectedProduct.order.amount = this.selectedProduct.order.internalTransferAmount;
            this.selectedProduct.order.shares = this.product
                ? Number((this.selectedProduct.order.amount / this.product.lastPrice).toFixed(8))
                : 0;

            if (this.amountDirective) {
                this.amountDirective.value = this.selectedProduct.order.internalTransferAmount;
            }

            this.cdr.detectChanges();
        });
    }

    onAmountChange() {
        setTimeout(() => {
            if (this.amountDirective) {
                this.selectedProduct.order.internalTransferAmount = this.amountDirective.value;
            }

            const sharePrice = this.internalProduct ? this.internalProduct.amount / this.internalProduct.shares : 0;
            this.selectedProduct.order.internalTransferShares = sharePrice
                ? Number((this.selectedProduct.order.internalTransferAmount / sharePrice).toFixed(8))
                : 0;

            this.selectedProduct.order.amount = this.selectedProduct.order.internalTransferAmount;
            this.selectedProduct.order.shares = this.product
                ? Number((this.selectedProduct.order.amount / this.product.lastPrice).toFixed(8))
                : 0;

            if (this.sharesDirective) {
                this.sharesDirective.value = this.selectedProduct.order.internalTransferShares;
            }

            this.cdr.detectChanges();
        });
    }

    onExonerate() {
        this.exonerate.emit(this.product.id as Id);
    }
}
